import { gql, NetworkStatus, useQuery } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import cx from 'classnames'
import { Link } from 'gatsby'
import { FaSync } from 'react-icons/fa'
import { DiscountCodeDetailsQuery, DiscountCodeDetailsQueryVariables } from './__generated__/DiscountCodeDetailsQuery'

const discountCodeDetailsQuery = gql`
  query DiscountCodeDetailsQuery($thirdPartyId: ID!, $discountCode: ID!) {
    thirdPartyById(id: $thirdPartyId) {
      discountCode(code: $discountCode) {
        code
        startDate
        endDate
        batchName

        kits {
          id
          status
          sku
        }
      }
    }
  }
`

interface DiscountCodeDetailsProps {
  thirdPartyId: string
  discountCode: string
}

const DiscountCodeDetails = (props: RouteComponentProps<DiscountCodeDetailsProps>) => {
  const { loading, error, data, refetch, networkStatus } = useQuery<
    DiscountCodeDetailsQuery,
    DiscountCodeDetailsQueryVariables
  >(discountCodeDetailsQuery, {
    variables: {
      thirdPartyId: props.thirdPartyId,
      discountCode: props.discountCode,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  if (loading && !data) return <p>Loading...</p>

  if (error) {
    throw error
  }

  const discountCode = data.thirdPartyById?.discountCode

  if (!discountCode) return <p>Not Found</p>

  return (
    <>
      <nav className="breadcrumb ml-6" aria-label="breadcrumbs">
        <ul>
          <li>
            <Link to="/dashboard/third-parties">Third Parties</Link>
          </li>
          <li>
            <a href={`/dashboard/third-party/${props.thirdPartyId}`}>{props.thirdPartyId}</a>
          </li>
          <li className="is-active">
            <a
              href={`/dashboard/third-party/${props.thirdPartyId}/discount-code/${props.discountCode}`}
              aria-current="page"
            >
              Discount Code {discountCode.code}
            </a>
          </li>
        </ul>
      </nav>

      <section className="section is-small">
        <div className="container">
          <h1 className="title">
            Discount Code <span className="is-family-code has-text-primary">{discountCode.code}</span>
          </h1>

          <p>
            <strong>Start Date: </strong>
            {discountCode.startDate}
          </p>
          <p>
            <strong>End Date: </strong>
            {discountCode.endDate}
          </p>
          <p>
            <strong>Reference / Batch Name: </strong>
            {discountCode.batchName}
          </p>
        </div>
      </section>

      <section className="section mt-5">
        <nav className="level has-width-unset">
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Kits</p>
              <p className="title">{discountCode.kits.length}</p>
            </div>
          </div>

          <div className="level-item">
            <button
              className={cx('button is-info is-outlined', {
                'is-loading': networkStatus === NetworkStatus.refetch,
              })}
              onClick={() => refetch()}
            >
              <span className="icon is-small">
                <FaSync />
              </span>
            </button>
          </div>
        </nav>
        <div className="container">
          <table className="table is-bordered is-fullwidth">
            <thead>
              <tr>
                <th>Kit ID</th>
                <th>SKU</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {discountCode.kits.map((kit) => (
                <tr key={kit.id}>
                  <td>
                    <Link to={`/dashboard/kits?kitId=${kit.id}`}>{kit.id}</Link>
                  </td>
                  <td>{kit.sku}</td>
                  <td>{kit.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}

export default DiscountCodeDetails
